"use client";

import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { setCurrency, setCurrencyFromLocation, setLocationFetched, Currency } from '@/app/store/slices/currencySlice';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const currencies: { code: Currency; label: string; symbol: string }[] = [
    { code: 'INR', label: 'India', symbol: '₹' },
    { code: 'USD', label: 'Global', symbol: '$' },
    { code: 'EUR', label: 'Europe', symbol: '€' },
];

const EUR_COUNTRIES = new Set([
    'DE','FR','IT','ES','NL','BE','AT','FI','PT','IE','GR',
    'SK','SI','EE','LV','LT','LU','MT','CY','HR','AD','MC','SM','VA'
]);

function getCurrencyFromCountry(countryCode: string): Currency {
    if (countryCode === 'IN') return 'INR';
    if (EUR_COUNTRIES.has(countryCode)) return 'EUR';
    return 'USD';
}

export const CurrencySelector = ({ className = "" }: { className?: string }) => {
    const dispatch = useAppDispatch();
    const selectedCurrency = useAppSelector((state) => state.currency.selectedCurrency);
    const hasBeenManuallySet = useAppSelector((state) => state.currency.hasBeenManuallySet);
    const locationFetched = useAppSelector((state) => state.currency.locationFetched);

    useEffect(() => {
        if (hasBeenManuallySet || locationFetched) return;
        
        // Dispatch immediately to prevent other instances from starting the fetch
        dispatch(setLocationFetched(true));

        fetch('https://ipapi.co/json/?key=itxzaCodCHu52J74Btvpk3x3fiqxWujS9wbEuMHPy1NBfgefNh')
            .then((res) => res.json())
            .then((data) => {
                if (data?.country_code) {
                    dispatch(setCurrencyFromLocation(getCurrencyFromCountry(data.country_code)));
                }
            })
            .catch(() => {
                // If it fails, we keep locationFetched as true to avoid retrying continuously
                // but you might want to retry on next session.
                /* silently fall back to default */
            });
    }, [hasBeenManuallySet, locationFetched, dispatch]);

    const handleCurrencyChange = (currency: Currency) => {
        dispatch(setCurrency(currency));
    };

    const currentCurrency = currencies.find(c => c.code === selectedCurrency) || currencies[0];

    return (
        <div className={`relative ${className}`}>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-blue-900 transition-colors bg-white/50 backdrop-blur-sm rounded-full border border-slate-200/60 hover:border-blue-200 active:scale-95"
                        aria-label="Select Currency"
                    >
                        <Globe size={14} className="text-slate-400 group-hover:text-blue-600" />
                        <span className="flex items-center gap-1">
                            {selectedCurrency} 
                            <span className="text-slate-400 font-medium">({currentCurrency.symbol})</span>
                        </span>
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-36 mt-2 rounded-xl border-slate-100 shadow-xl overflow-hidden p-1">
                    {currencies.map((currency) => (
                        <DropdownMenuItem
                            key={currency.code}
                            onClick={() => handleCurrencyChange(currency.code)}
                            className={`flex items-center justify-between px-3 py-2 text-xs font-bold cursor-pointer rounded-lg transition-colors ${
                                selectedCurrency === currency.code 
                                ? 'bg-blue-50 text-blue-700' 
                                : 'text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            <div className="flex flex-col">
                                <span className="text-[10px] text-slate-400 font-medium">{currency.label}</span>
                                <span>{currency.code}</span>
                            </div>
                            <span className="text-sm opacity-60">{currency.symbol}</span>
                            
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};
